<template>
  <div v-if="!isRecurringCalendarSelected" class="rounded-lg bg-gray-50 p-4">
    <div
      v-if="timezone"
      class="flex flex-row items-center gap-2 border-b border-gray-200 py-3"
    >
      <Globe05Icon class="h-5 w-5 text-gray-500" />
      {{
        computedTimezones
          .map(x => x.children)
          .flat()
          .find(x => x.value === timezone)?.label
      }}
    </div>
    <div v-if="groupedItems.length > 0" class="flex flex-col">
      <div
        v-for="(group, index) in groupedItems"
        :key="index"
        class="flex flex-row items-start bg-gray-50 p-3"
        :class="{
          'border-b-[1.5px] border-gray-200 bg-gray-50':
            index !== groupedItems.length - 1,
        }"
      >
        <div class="flex items-start justify-between">
          <div class="flex flex-col">
            <span class="text-sm font-medium text-gray-900">
              {{ group.displayText }}
            </span>
            <div class="mt-1 flex flex-col">
              <template v-if="group.intervals && group.intervals.length > 0">
                <span
                  v-for="(slot, i) in group.intervals"
                  :key="i"
                  class="py-1 text-sm text-gray-500"
                >
                  {{ formatTime(slot.from) }} - {{ formatTime(slot.to) }}
                </span>
              </template>
              <span v-else class="py-1 text-sm text-gray-500">
                {{ $t('calendar_advanced.availability.unavailable_label') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- empty state -->
    <div v-else class="flex h-[500px] flex-col items-center justify-center">
      <img
        :src="DateSpecificHours"
        class="mb-2 h-36"
        :alt="$t('alt_text.date_specific_hours')"
      />
      <p class="text-md text-center font-semibold text-gray-900">
        {{ $t('calendar_advanced.availability.no_date_specific_heading') }}
      </p>

      <p class="text-center text-xs text-gray-600">
        {{ $t('calendar_advanced.availability.no_date_specific_description') }}
      </p>
    </div>
  </div>
  <!-- recurring calendar selected -->
  <div v-else class="rounded-lg bg-gray-50 p-4">
    <div class="flex flex-row items-center gap-2 border-b border-gray-200 py-3">
      <Globe05Icon class="h-5 w-5 text-gray-500" />
      {{
        computedTimezones
          .map(x => x.children)
          .flat()
          .find(x => x.value === timezone)?.label
      }}
    </div>
    <div
      class="flex h-[500px] flex-col items-center justify-center rounded-lg bg-gray-50 p-4"
    >
      <img
        :src="RecurringDateSpecificHours"
        class="mb-2 h-36"
        :alt="$t('alt_text.date_specific_hours')"
      />
      <p class="text-center text-xs text-gray-500">
        {{
          $t(
            'settings.availability.schedule.date_specific_hours_recurring_meetings_enabled'
          )
        }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import DateSpecificHours from '@/assets/date-specific-hours.png'
import RecurringDateSpecificHours from '@/assets/date-specific-recurring.png'
import { computedTimezones } from '@/utils/timezonesOptions'
import { groupConsecutiveDates } from '@gohighlevel/ghl-calendar-element'
import { Globe05Icon } from '@gohighlevel/ghl-icons/24/outline'
import moment from 'moment'
import { computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import AppState from '../../states/app'
import { DatePickerLanguageMapper } from '../../utils/constants'
import { Schedule } from '../Schedule/Schedule'

const { t } = useI18n()

const props = defineProps({
  schedule: {
    type: Object as PropType<Schedule>,
    required: true,
  },
  isRecurringCalendarSelected: {
    type: Boolean,
    required: true,
  },
})

const timezone = computed(() => props.schedule.timezone)

const locale = computed(() => {
  return DatePickerLanguageMapper[AppState.locale]
})

// Group consecutive dates with the same hours using shared utility
const groupedItems = computed(() => {
  const items = props.schedule?.getDateSpecificHours() || []
  return groupConsecutiveDates(items, locale.value)
})

const formatDate = (date: string) => {
  return moment(date).format('MMMM D, YYYY')
}

function formatTime(time: string) {
  // Expects 'H:m' or 'HH:mm', returns 'hh:mm AM/PM'
  if (!time) {
    return ''
  }
  const [h, m] = time.split(':').map(Number)
  if (isNaN(h) || isNaN(m)) {
    return time
  }
  const date = new Date()
  date.setHours(h)
  date.setMinutes(m)
  return date
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase()
}
</script>
